import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Pagination, Spinner } from '@/components/ui'
import TaskItem from '../renew-tasks/TaskItem'
import { completeRenewTask } from '../renew-tasks/renewTasksSlice'
import useRenewTasks from './RenewTasksContext'

/**
 * Widget dashboard pour afficher les tâches de renouvellement à venir
 * Filtrées par type (assurance, diagnostic, entretien, facturation)
 * Utilise le hook global useRenewTasks qui initialise et partage les données
 *
 * @param {string} task_type - Type de tâche (assurance, diagnostic, entretien, facturation)
 * @param {number} daysWindow - Fenêtre de jours (default: 30)
 * @param {number} maxTasks - Limite de tâches à afficher (default: 10)
 */
export default ({ task_type = 'assurance', daysWindow = 30, maxTasks = 10 }) => {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const tasksPerPage = 6 // Afficher 6 tâches par page (3 items x 2 colonnes)

  // Utiliser le hook global qui initialise et récupère les données
  const { loading, tasksByType } = useRenewTasks()

  // Récupérer les tâches et la configuration du type
  const { tasks = [], typeConfig = {} } = tasksByType[task_type] || {}

  const handleComplete = (taskId) => {
    return dispatch(completeRenewTask(taskId))
  }

  // Pagination
  const totalPages = Math.ceil(tasks.length / tasksPerPage)
  const startIndex = (currentPage - 1) * tasksPerPage
  const endIndex = startIndex + tasksPerPage
  const currentTasks = tasks.slice(startIndex, endIndex)

  const handlePageChange = (page) => setCurrentPage(page)

  // Reset page quand les tâches changent
  useEffect(() => {
    setCurrentPage(1)
  }, [task_type, tasks.length])

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">{typeConfig?.title || task_type}</h2>
      </div>

      <div className="flex flex-col h-100 rounded-none bg-gray-200">
        <div className="flex-1 p-2 overflow-y-auto">
          {loading && (
            <div className="flex justify-center p-4">
              <Spinner color="primary" />
            </div>
          )}

          {!loading && tasks.length === 0 && (
            <div className="text-center p-4 text-gray-600">
              Aucune tâche à venir
            </div>
          )}

          {!loading && currentTasks.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {currentTasks.map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onComplete={handleComplete}
                  type="renew"
                  showDetails={true}
                  typeConfig={typeConfig}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="p-2 border-t border-gray-300 bg-gray-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="my-2"
            />
          </div>
        )}
      </div>
    </>
  )
}
